import { NOTIFICATION_OPERATOR_RECIPIENTS_MAX } from '@repo/api-contracts';
import { type FormEvent, type ReactElement, useId, useState } from 'react';
import { Button, Input, Label } from '@/shared/ui';
import { useAddNotificationOperatorRecipient } from '../model/use-add-notification-operator-recipient';

interface OperatorRecipientFormProps {
  /** 이미 상한(10명)이면 칸과 버튼을 잠근다 — 서버도 같은 상한으로 거부한다. */
  readonly isFull: boolean;
}

const LIMIT_NOTICE = `수신자는 ${NOTIFICATION_OPERATOR_RECIPIENTS_MAX}명까지 등록할 수 있습니다. 추가하려면 먼저 한 명을 삭제해 주세요.`;

/** 운영자 수신자 추가 폼(알림 설계 §6-2) — 이메일 하나를 받아 등록한다. 성공하면 칸을 비운다. */
export const OperatorRecipientForm = ({ isFull }: OperatorRecipientFormProps): ReactElement => {
  const [email, setEmail] = useState('');
  const inputId = useId();
  const messageId = `${inputId}-message`;
  const limitId = `${inputId}-limit`;
  const { addRecipient, isPending, fieldError, formError } = useAddNotificationOperatorRecipient();
  const message = fieldError ?? formError;
  const describedBy =
    [message ? messageId : null, isFull ? limitId : null].filter(Boolean).join(' ') || undefined;

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();

    if (await addRecipient(email)) {
      setEmail('');
    }
  };

  return (
    <form
      aria-label="운영자 수신자 추가"
      noValidate
      onSubmit={(event) => {
        void handleSubmit(event);
      }}
      className="flex flex-col gap-2"
    >
      <Label htmlFor={inputId}>수신 이메일</Label>
      <div className="flex flex-wrap items-center gap-2">
        <Input
          id={inputId}
          type="email"
          autoComplete="off"
          className="w-full max-w-sm"
          placeholder="ops@example.com"
          value={email}
          disabled={isFull || isPending}
          hasError={Boolean(fieldError)}
          aria-describedby={describedBy}
          onChange={(event) => setEmail(event.target.value)}
        />
        <Button type="submit" variant="outline" isLoading={isPending} disabled={isFull}>
          추가
        </Button>
      </div>
      {isFull ? (
        <p id={limitId} className="text-xs text-muted-foreground">
          {LIMIT_NOTICE}
        </p>
      ) : null}
      {message ? (
        <p id={messageId} role="alert" className="text-sm text-destructive">
          {message}
        </p>
      ) : null}
    </form>
  );
};
